import { useState, useEffect } from "react";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useAuth from "../../../hooks/useAuth";
import LoadingSpinner from "../../loadingSpinner/LoadingSpinner";
import "../../../css/RolePage.css"; // reuse the card styles
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function FavouriteSteps({ isNavOpen, customRoles }) {
  const { auth } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [favoriteSteps, setFavoriteSteps] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatingFavorites, setUpdatingFavorites] = useState({});

  const userId = auth?.userId;

  useEffect(() => {
    if (!userId) return;

    const fetchFavorites = async () => {
      try {
        setIsLoading(true);
        const res = await axiosPrivate.get(`/steps/favorites/${userId}`);
        setFavoriteSteps(res.data);
      } catch (err) {
        console.error("❌ Error fetching favorites:", err);
        setError(
          err.response?.data?.message ||
          t("favouriteSteps.errorFetch")
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchFavorites();
  }, [axiosPrivate, userId, t]);

  // ✅ Unfavorite step
  const toggleFavorite = async (stepId) => {
    if (!userId) return;
    setUpdatingFavorites((prev) => ({ ...prev, [stepId]: true }));

    try {
      await axiosPrivate.delete(`/steps/${stepId}/favorite`, {
        data: { userId },
      });
      // Optimistically remove from UI
      setFavoriteSteps((prev) =>
        prev.filter((step) => step.step_id !== stepId)
      );
    } catch (err) {
      console.error("❌ Error removing favorite:", err);
    } finally {
      setUpdatingFavorites((prev) => ({ ...prev, [stepId]: false }));
    }
  };

  // ✅ Group favorites by role
  const getStepsForRole = (roleId) => {
    return favoriteSteps.filter((step) => step.role_ids?.includes(roleId));
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <p className="error-message">{error}</p>;

  const rolesWithSteps = customRoles.filter(
    (role) => getStepsForRole(role.role_id).length > 0
  );

  return (
    <div className={`${isNavOpen ? "body-squeezed" : "body"}`}>
      <div className="admin-steps">
        <h2 style={{ marginBottom: "2em", alignSelf: "center" }}>
          {t("favouriteSteps.title")}
        </h2>

        {rolesWithSteps.length === 0 ? (
          <p style={{ color: "gray", marginTop: "2rem" }}>
            {t("favouriteSteps.none")}
          </p>
        ) : (
          rolesWithSteps.map((role) => {
            const roleSteps = getStepsForRole(role.role_id);
            return (
              <div key={role.role_id} style={{ marginBottom: "3rem" }}>
                <h3
                  style={{
                    marginBottom: "1rem",
                    textAlign: "center",
                    color: "var(--color400, #000)",
                  }}
                >
                  {role.role_name}
                </h3>

                <div className="steps-grid">
                  {roleSteps.map((step) => {
                    const isToggling = updatingFavorites[step.step_id];
                    return (
                      <div
                        key={step.step_id}
                        className="step-card"
                        onClick={() =>
                          navigate(
                            `/roles/${role.role_slug}/steps/${step.step_id}`
                          )
                        }
                        style={{ cursor: "pointer" }}
                      >
                        {/* Star button — unfavorite */}
                        <button
                          className="star-button"
                          onClick={(e) => {
                            e.stopPropagation(); // prevent navigating
                            toggleFavorite(step.step_id);
                          }}
                          disabled={isToggling}
                          title={t(
                            "favouriteSteps.removeFromFavorites"
                          )}
                        >
                          ★
                        </button>

                        <div className="step-name">{step.step_name}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
